import React, { useState } from 'react';
import { Button, Card, Row, Col, Badge, Modal } from 'react-bootstrap';
import { FaPlus, FaComments, FaEdit, FaTrash, FaProjectDiagram, FaListUl } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Flow } from '../flows/types';
import FlowEditor from '../flows/FlowEditor';
import FlowDiagramEditor from '../flows/FlowDiagramEditor';
import { Icon } from '../../../../../components/common/Icon';
import ConfirmationModal from '../../../../../components/common/ConfirmationModal';
import { ChatbotFormData } from '../types';

interface FlowsTabProps {
    formData: ChatbotFormData;
    setFormData: React.Dispatch<React.SetStateAction<ChatbotFormData>>;
    isEditMode?: boolean;
}

const FlowsTab: React.FC<FlowsTabProps> = ({ formData, setFormData, isEditMode }) => {
    const { t } = useTranslation();
    const [showAddChoiceModal, setShowAddChoiceModal] = useState(false);
    /** 'visual' | 'classic' = editor open in-place under Interactive Flows; null = list view */
    const [inPlaceEditor, setInPlaceEditor] = useState<'visual' | 'classic' | null>(null);
    const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
    const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);

    const flows: Flow[] = (formData.flows as Flow[]) || [];

    const openAddFlowChoice = () => {
        setEditingFlow(null);
        setShowAddChoiceModal(true);
    };

    const openEditorWithChoice = (mode: 'classic' | 'visual') => {
        setShowAddChoiceModal(false);
        setInPlaceEditor(mode);
    };

    const handleEdit = (flow: Flow) => {
        setEditingFlow(flow);
        setShowAddChoiceModal(true);
    };

    const handleDeleteClick = (flow: Flow) => {
        setFlowToDelete(flow);
    };

    const handleConfirmDeleteFlow = () => {
        if (flowToDelete) {
            const updatedFlows = flows.filter(f => f.id !== flowToDelete.id);
            setFormData(prev => ({ ...prev, flows: updatedFlows }));
            setFlowToDelete(null);
            toast.success(t('flows.flow_deleted_successfully', 'Flow deleted successfully.'));
        }
    };

    const handleCancelDeleteFlow = () => {
        setFlowToDelete(null);
    };

    const handleSaveFlow = (flow: Flow) => {
        let updatedFlows = [...flows];
        const index = updatedFlows.findIndex(f => f.id === flow.id);
        const isEdit = index >= 0;

        if (isEdit) {
            updatedFlows[index] = flow;
        } else {
            updatedFlows.push(flow);
        }

        setFormData(prev => ({ ...prev, flows: updatedFlows }));
        setInPlaceEditor(null);
        setEditingFlow(null);
        toast.success(isEdit ? t('flows.flow_updated_successfully', 'Flow updated successfully.') : t('flows.flow_added_successfully', 'Flow added successfully.'));
    };

    const handleCloseEditor = () => {
        setInPlaceEditor(null);
        setEditingFlow(null);
    };

    // Editor in-place (Visual or Classic) under Interactive Flows
    if (inPlaceEditor === 'visual') {
        return (
            <div className="flows-tab flows-tab-editor p-0 animate-fade-in d-flex flex-column" style={{ minHeight: '70vh' }}>
                <FlowDiagramEditor
                    initialFlow={editingFlow}
                    onSave={handleSaveFlow}
                    onCancel={handleCloseEditor}
                />
            </div>
        );
    }
    if (inPlaceEditor === 'classic') {
        return (
            <div className="flows-tab flows-tab-editor p-0 animate-fade-in d-flex flex-column" style={{ minHeight: '70vh' }}>
                <div className="p-4">
                    <FlowEditor
                        initialFlow={editingFlow}
                        onSave={handleSaveFlow}
                        onCancel={handleCloseEditor}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flows-tab p-4 animate-fade-in">
            {/* Header: title + single Add Flow button */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1 fw-bold text-dark">Interactive Flows</h4>
                    <p className="text-muted mb-0 small">
                        Create structured conversations that trigger on specific questions.
                    </p>
                </div>
                <Button
                    onClick={openAddFlowChoice}
                    className="chatbots-actions-save d-flex align-items-center gap-2"
                >
                    <Icon icon={FaPlus} />
                    <span>Add Flow</span>
                </Button>
            </div>

            {/* Empty state */}
            {flows.length === 0 ? (
                <Card className="flows-empty-card glass-card border-0 text-center overflow-hidden">
                    <Card.Body className="py-5 px-4">
                        <div className="flows-empty-icon-wrapper mb-3">
                            <Icon icon={FaComments} size={40} className="text-primary opacity-75" />
                        </div>
                        <h5 className="fw-bold text-dark mb-2">No flows yet</h5>
                        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '420px' }}>
                            Flows let your chatbot collect information step by step (e.g. email, phone) when the user says specific trigger phrases.
                        </p>
                        <Button
                            onClick={openAddFlowChoice}
                            className="chatbots-actions-save d-inline-flex align-items-center gap-2"
                        >
                            <Icon icon={FaPlus} />
                            <span>Add your first flow</span>
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                /* Flow cards grid */
                <Row className="g-4 flows-grid">
                    {flows.map(flow => (
                        <Col xs={12} sm={6} lg={4} key={flow.id}>
                            <Card className="flows-card glass-card h-100 border-0 overflow-hidden shadow-sm position-relative">
                                <div className="flows-card-accent" />
                                <Card.Body className="p-4 position-relative">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <Badge
                                            className={`flows-status-badge ${flow.is_active ? 'flows-status-active' : 'flows-status-inactive'}`}
                                        >
                                            <span className="flows-status-dot" />
                                            {flow.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                        <div className="d-flex gap-1 flows-card-actions">
                                            <Button
                                                size="sm"
                                                variant="light"
                                                className="flows-card-action-btn rounded-2"
                                                onClick={() => handleEdit(flow)}
                                                title="Edit flow"
                                            >
                                                <FaEdit className="text-primary" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="light"
                                                className="flows-card-action-btn rounded-2"
                                                onClick={() => handleDeleteClick(flow)}
                                                title="Delete"
                                            >
                                                <FaTrash className="text-danger" />
                                            </Button>
                                        </div>
                                    </div>

                                    <h5 className="flows-card-title fw-bold text-dark mb-3" title={flow.name}>
                                        {flow.name}
                                    </h5>

                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        <span className="flows-meta-pill flows-meta-steps">
                                            {flow.steps.length} step{flow.steps.length !== 1 ? 's' : ''}
                                        </span>
                                        <span className="flows-meta-pill flows-meta-triggers">
                                            {flow.trigger_keywords.length} trigger{flow.trigger_keywords.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    <div className="flows-triggers-block">
                                        <small className="text-muted text-uppercase fw-semibold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                            Trigger keywords
                                        </small>
                                        <div className="text-dark small">
                                            {flow.trigger_keywords.length > 0
                                                ? flow.trigger_keywords.join(', ')
                                                : <span className="fst-italic text-muted">None defined</span>}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Modal 1: Choose Visual or Classic */}
            <Modal
                show={showAddChoiceModal}
                onHide={() => setShowAddChoiceModal(false)}
                centered
                className="flows-choice-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        {editingFlow ? 'Edit flow' : 'Add new flow'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 pb-4">
                    <p className="text-muted small mb-4">
                        {editingFlow
                            ? 'Choose how you want to edit this flow.'
                            : 'Choose how you want to create the flow.'}
                    </p>
                    <Row className="g-3">
                        <Col xs={12} md={6}>
                            <button
                                type="button"
                                className="flows-choice-option w-100 p-4 rounded-3 border text-start"
                                onClick={() => openEditorWithChoice('visual')}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div className="flows-choice-icon flows-choice-icon-visual">
                                        <Icon icon={FaProjectDiagram} size={24} />
                                    </div>
                                    <div>
                                        <span className="fw-bold d-block text-dark">Visual</span>
                                        <span className="small text-muted">Diagram editor with nodes and connections</span>
                                    </div>
                                </div>
                            </button>
                        </Col>
                        <Col xs={12} md={6}>
                            <button
                                type="button"
                                className="flows-choice-option w-100 p-4 rounded-3 border text-start"
                                onClick={() => openEditorWithChoice('classic')}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div className="flows-choice-icon flows-choice-icon-classic">
                                        <Icon icon={FaListUl} size={24} />
                                    </div>
                                    <div>
                                        <span className="fw-bold d-block text-dark">Classic</span>
                                        <span className="small text-muted">Form-based steps and actions</span>
                                    </div>
                                </div>
                            </button>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>

            <ConfirmationModal
                show={flowToDelete != null}
                onConfirm={handleConfirmDeleteFlow}
                onCancel={handleCancelDeleteFlow}
                title={t('flows.delete_flow_title', 'Delete flow')}
                message={t('flows.delete_flow_message', 'Are you sure you want to delete this flow? This action cannot be undone.')}
                confirmText="delete"
                cancelText="cancel"
            />
        </div>
    );
};

export default FlowsTab;
